import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, FileDown, Globe, Mail, Phone, Plus, ShieldCheck, Trash2, X,
} from "lucide-react";
import { API } from "@/lib/api";

const STEPS = [
  "Who Needs Coverage",
  "Coverage Goals",
  "Protection Needs",
  "Health Basics",
  "Current Coverage",
  "Contact",
];

const REASONS = [
  "Income replacement", "Mortgage / home protection", "Protect spouse / children",
  "Final expenses", "Child permanent coverage", "Cash-value / long-term planning",
  "Business protection", "Legacy / inheritance", "Living benefits", "Not sure", "Other",
];

const COVERAGE_AMOUNTS = [
  "$25,000 or less", "$50,000", "$100,000", "$250,000", "$500,000", "$750,000",
  "$1,000,000+", "Not sure — help me calculate it",
];

const COVERAGE_TYPES = [
  "Term Life", "Term with Living Benefits", "Whole Life", "Child Whole Life",
  "Universal Life", "Indexed Universal Life (IUL)", "Final Expense",
  "Mortgage Protection / Home Certainty", "Permanent Adult / Provider Whole Life",
  "Not sure — show me my options",
];

const INCOME_OPTIONS = ["Under $2,000", "$2,000–$4,000", "$4,000–$6,000", "$6,000–$10,000", "$10,000+", "Not sure"];

const HELP_WITH = [
  "Mortgage", "Rent", "Car payments", "Childcare", "College / education", "Household bills",
  "Debt", "Income replacement", "Business obligations", "Final expenses", "Legacy / inheritance", "Other",
];

const CONDITIONS = [
  "No major conditions", "High blood pressure", "Diabetes", "Heart condition", "Cancer",
  "Stroke", "Respiratory condition", "Mental health condition", "Other",
];

const emptyInsured = () => ({
  full_name: "", dob: "", gender: "", state: "", relationship: "",
  smoker: "", coverage_amount: "", reason: "",
});

const initialForm = {
  coverage_for: "",
  insureds: [emptyInsured()],
  coverage_amount: "",
  coverage_types: [],
  financial: {
    monthly_income: "", has_mortgage: "", mortgage_balance: "", mortgage_payment: "",
    has_dependents: "", dependents_count: "", help_with: [],
  },
  health: {
    height: "", weight: "", tobacco: "", tobacco_details: "",
    conditions: [], condition_notes: "", medications: "", medication_names: "",
  },
  current_coverage: {
    has_life_insurance: "", existing_amount: "", existing_type: "",
    existing_ownership: "", looking_to: "",
  },
  contact: {
    full_name: "", phone: "", email: "", preferred_contact: "", best_time: "",
    consent_info: false, consent_contact: false,
  },
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Q = ({ children }) => (
  <p className="font-serif text-xl sm:text-2xl font-semibold text-[#0B2319] mb-4">{children}</p>
);

const Sub = ({ children }) => (
  <p className="text-sm text-[#5A6B63] mb-5 -mt-2 leading-relaxed">{children}</p>
);

const OptionPills = ({ options, value, onChange, testIdPrefix }) => (
  <div className="flex flex-wrap gap-2.5">
    {options.map((o) => {
      const active = value === o;
      return (
        <button key={o} type="button" data-testid={`${testIdPrefix}-${slug(o)}`}
                onClick={() => onChange(o)}
                className={`px-5 py-3 rounded-full border text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#0B2319] text-[#FCF6BA] border-[#D4AF37]"
                    : "bg-[#FAF7F0] text-[#1A231F] border-[#D4AF37]/30 hover:border-[#D4AF37]"
                }`}>
          {o}
        </button>
      );
    })}
  </div>
);

const CheckPills = ({ options, values, onToggle, testIdPrefix }) => (
  <div className="flex flex-wrap gap-2.5">
    {options.map((o) => {
      const active = values.includes(o);
      return (
        <button key={o} type="button" data-testid={`${testIdPrefix}-${slug(o)}`}
                onClick={() => onToggle(o)}
                className={`px-5 py-3 rounded-full border text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                  active
                    ? "bg-[#0B2319] text-[#FCF6BA] border-[#D4AF37]"
                    : "bg-[#FAF7F0] text-[#1A231F] border-[#D4AF37]/30 hover:border-[#D4AF37]"
                }`}>
          {active && <Check size={14} />} {o}
        </button>
      );
    })}
  </div>
);

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="block text-sm font-semibold text-[#0B2319] mb-1.5">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-[#D4AF37]/30 bg-white text-[#1A231F] text-sm focus:outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]";

export default function QuoteForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (path, value) => {
    setForm((f) => {
      const copy = structuredClone(f);
      const keys = path.split(".");
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const toggleArr = (path, item) => {
    const arr = path.split(".").reduce((o, k) => o[k], form);
    set(path, arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item]);
  };

  const setInsured = (idx, key, value) => {
    setForm((f) => {
      const copy = structuredClone(f);
      copy.insureds[idx][key] = value;
      return copy;
    });
  };

  const isMultiple = form.coverage_for === "Multiple people";

  const pickCoverageFor = (v) => {
    setForm((f) => {
      const copy = structuredClone(f);
      copy.coverage_for = v;
      if (v === "Myself") {
        copy.insureds = [{ ...copy.insureds[0], relationship: "Self" }];
      }
      return copy;
    });
  };

  const canContinue = () => {
    if (step === 0) {
      return form.coverage_for && form.insureds.every((p) => p.full_name.trim());
    }
    if (step === 5) {
      const c = form.contact;
      return c.full_name.trim() && c.phone.trim() && c.email.trim() && c.consent_info && c.consent_contact;
    }
    return true;
  };

  const next = () => {
    if (!canContinue()) {
      toast.error(step === 0
        ? "Please choose who needs coverage and enter each person's full name."
        : "Please complete your contact details and both consent checkboxes.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!canContinue()) {
      toast.error("Please complete your contact details and both consent checkboxes.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong submitting your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col" data-testid="quote-success-screen">
        <SimpleHeader />
        <div className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="card-gold max-w-xl w-full p-8 sm:p-10 text-center fade-up">
            <div className="w-16 h-16 rounded-full bg-[#0B2319] border border-[#D4AF37] mx-auto flex items-center justify-center">
              <ShieldCheck size={28} className="text-[#D4AF37]" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B2319] mt-6">
              YOUR QUOTE REQUEST IS IN.
            </h1>
            <p className="font-serif italic text-lg text-[#B8860B] mt-2">
              Thank you for trusting Simonne &amp; Co. Financial Solutions.
            </p>
            <p className="text-sm sm:text-base text-[#5A6B63] mt-4 leading-relaxed">
              I'll review your information and compare available options based on your coverage goals.
              If additional information is needed, I'll contact you before preparing your personalized
              recommendations.
            </p>
            <div className="gold-rule my-6" />
            <div className="text-sm text-[#1A231F] space-y-1.5">
              <p className="font-serif font-semibold text-base text-[#0B2319]">Simonne &amp; Co. Financial Solutions</p>
              <p className="flex items-center justify-center gap-2"><Phone size={13} className="text-[#C5A059]" /> 205-600-3708</p>
              <p className="flex items-center justify-center gap-2"><Mail size={13} className="text-[#C5A059]" /> simonnecofinancial@gmail.com</p>
              <p className="flex items-center justify-center gap-2"><Globe size={13} className="text-[#C5A059]" /> simonnecofinancial.com</p>
            </div>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://simonnecofinancial.com" target="_blank" rel="noopener noreferrer"
                 data-testid="visit-website-button"
                 className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-xl bg-[#0B2319] text-[#FCF6BA] font-semibold text-sm border border-[#D4AF37]/40 hover:bg-[#081A13] transition-colors">
                <Globe size={16} /> VISIT MY WEBSITE
              </a>
              <a href="/assets/policy-guide.pdf" download data-testid="success-download-guide-button"
                 className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-xl border border-[#D4AF37] text-[#0B2319] font-semibold text-sm hover:bg-[#FAF4E8] transition-colors">
                <FileDown size={16} className="text-[#B8860B]" /> DOWNLOAD POLICY GUIDE
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="quote-form-page">
      <SimpleHeader />

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="flex items-center justify-between text-xs text-[#5A6B63] mb-2">
          <span data-testid="step-indicator">Step {step + 1} of {STEPS.length}</span>
          <span className="font-semibold text-[#0B2319]" data-testid="step-title-label">{STEPS[step]}</span>
        </div>
        <div className="h-2 rounded-full bg-[#EFE9DC] overflow-hidden" data-testid="progress-bar">
          <div className="h-full rounded-full bg-gradient-to-r from-[#B8860B] to-[#D4AF37] transition-all duration-500"
               style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="card-gold p-6 sm:p-8 fade-up" key={step}>
          {step === 0 && <StepWho form={form} pickCoverageFor={pickCoverageFor} isMultiple={isMultiple}
                                  setInsured={setInsured} setForm={setForm} />}
          {step === 1 && <StepGoals form={form} set={set} toggleArr={toggleArr} />}
          {step === 2 && <StepFinancial form={form} set={set} toggleArr={toggleArr} />}
          {step === 3 && <StepHealth form={form} set={set} toggleArr={toggleArr} />}
          {step === 4 && <StepCurrent form={form} set={set} />}
          {step === 5 && <StepContact form={form} set={set} />}
        </div>

        <div className="flex gap-3 mt-6">
          {step > 0 && (
            <button type="button" onClick={back} data-testid="step-back-button"
                    className="h-14 px-6 rounded-xl border border-[#D4AF37]/50 text-[#0B2319] font-semibold text-sm inline-flex items-center gap-2 hover:bg-[#FAF4E8] transition-colors">
              <ArrowLeft size={16} /> Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={next} data-testid="step-continue-button"
                    className="flex-1 h-14 rounded-xl bg-[#0B2319] hover:bg-[#081A13] text-[#FCF6BA] font-semibold text-base border border-[#D4AF37]/40 inline-flex items-center justify-center gap-2 transition-colors">
              Continue <ArrowRight size={18} />
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={submitting} data-testid="submit-quote-button"
                    className="flex-1 h-14 rounded-xl bg-[#C5A059] hover:bg-[#B8860B] disabled:opacity-60 text-[#0B2319] font-bold text-base inline-flex items-center justify-center gap-2 transition-colors">
              {submitting ? "Submitting..." : "SUBMIT MY QUOTE REQUEST"} <Check size={18} />
            </button>
          )}
        </div>

        <p className="text-[11px] text-[#5A6B63] text-center mt-6 leading-relaxed" data-testid="form-compliance-note">
          Quotes are estimates only. Final rates, eligibility, product availability and coverage are
          subject to carrier underwriting and policy approval.
        </p>
      </div>
    </div>
  );
}

const SimpleHeader = () => (
  <header className="border-b border-[#D4AF37]/25 bg-[#FDFBF7]/90 backdrop-blur-md sticky top-0 z-40">
    <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5" data-testid="form-header-brand">
        <div className="w-9 h-9 rounded-full border border-[#D4AF37]/60 bg-[#0B2319] flex items-center justify-center">
          <span className="font-serif font-bold text-[#D4AF37] text-xs tracking-widest">SC</span>
        </div>
        <div>
          <p className="font-serif font-semibold text-[#0B2319] text-sm leading-tight">Simonne &amp; Co.</p>
          <p className="text-[9px] text-[#B8860B] tracking-[0.2em] uppercase">Protect. Plan. Build. Live.</p>
        </div>
      </Link>
      <a href="tel:2056003708" data-testid="form-header-phone"
         className="flex items-center gap-1.5 text-xs text-[#0B2319]">
        <Phone size={12} className="text-[#C5A059]" /> 205-600-3708
      </a>
    </div>
  </header>
);

function StepWho({ form, pickCoverageFor, isMultiple, setInsured, setForm }) {
  const options = ["Myself", "My spouse / partner", "My child", "Multiple people", "Someone else"];
  const persons = isMultiple ? form.insureds : form.insureds.slice(0, 1);

  return (
    <div data-testid="step-who">
      <Q>Who are you requesting coverage for?</Q>
      <OptionPills options={options} value={form.coverage_for} onChange={pickCoverageFor}
                   testIdPrefix="coverage-for" />

      {form.coverage_for && (
        <div className="mt-8 space-y-6">
          {persons.map((p, i) => (
            <div key={i} className="rounded-2xl border border-[#D4AF37]/30 bg-white p-5 space-y-4"
                 data-testid={`insured-card-${i}`}>
              <div className="flex items-center justify-between">
                <p className="font-serif font-semibold text-[#0B2319]">
                  {isMultiple ? `Person ${i + 1}` : "About the person needing coverage"}
                </p>
                {isMultiple && form.insureds.length > 1 && (
                  <button type="button" data-testid={`remove-person-button-${i}`}
                          onClick={() => setForm((f) => {
                            const copy = structuredClone(f);
                            copy.insureds.splice(i, 1);
                            return copy;
                          })}
                          className="text-[#5A6B63] hover:text-red-700 transition-colors">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <Field label="Full legal name" testId={`insured-name-field-${i}`}>
                <input className={inputCls} value={p.full_name} data-testid={`insured-name-input-${i}`}
                       onChange={(e) => setInsured(i, "full_name", e.target.value)} placeholder="First and last name" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date of birth" testId={`insured-dob-field-${i}`}>
                  <input type="date" className={inputCls} value={p.dob} data-testid={`insured-dob-input-${i}`}
                         onChange={(e) => setInsured(i, "dob", e.target.value)} />
                </Field>
                <Field label="Gender" testId={`insured-gender-field-${i}`}>
                  <select className={inputCls} value={p.gender} data-testid={`insured-gender-select-${i}`}
                          onChange={(e) => setInsured(i, "gender", e.target.value)}>
                    <option value="">Select</option>
                    <option>Female</option>
                    <option>Male</option>
                    <option>Other</option>
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="State of residence" testId={`insured-state-field-${i}`}>
                  <input className={inputCls} value={p.state} data-testid={`insured-state-input-${i}`}
                         onChange={(e) => setInsured(i, "state", e.target.value)} placeholder="e.g. AL" />
                </Field>
                <Field label="Relationship to you" testId={`insured-relationship-field-${i}`}>
                  <input className={inputCls} value={p.relationship} data-testid={`insured-relationship-input-${i}`}
                         onChange={(e) => setInsured(i, "relationship", e.target.value)}
                         placeholder={form.coverage_for === "Myself" ? "Self" : "e.g. Spouse"} />
                </Field>
              </div>
              <Field label="Smoker / nicotine use?" testId={`insured-smoker-field-${i}`}>
                <OptionPills options={["Yes", "No"]} value={p.smoker}
                             onChange={(v) => setInsured(i, "smoker", v)} testIdPrefix={`smoker-${i}`} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Desired coverage amount" testId={`insured-amount-field-${i}`}>
                  <select className={inputCls} value={p.coverage_amount} data-testid={`insured-amount-select-${i}`}
                          onChange={(e) => setInsured(i, "coverage_amount", e.target.value)}>
                    <option value="">Select</option>
                    {COVERAGE_AMOUNTS.map((a) => <option key={a}>{a}</option>)}
                  </select>
                </Field>
                <Field label="Primary reason for coverage" testId={`insured-reason-field-${i}`}>
                  <select className={inputCls} value={p.reason} data-testid={`insured-reason-select-${i}`}
                          onChange={(e) => setInsured(i, "reason", e.target.value)}>
                    <option value="">Select</option>
                    {REASONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          ))}

          {isMultiple && (
            <button type="button" data-testid="add-person-button"
                    onClick={() => setForm((f) => ({ ...f, insureds: [...f.insureds, emptyInsured()] }))}
                    className="w-full h-13 py-3.5 rounded-xl border-2 border-dashed border-[#D4AF37]/60 text-[#0B2319] font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#FAF4E8] transition-colors">
              <Plus size={16} /> ADD ANOTHER PERSON
            </button>
          )}
          {isMultiple && (
            <p className="text-xs text-[#5A6B63] text-center">
              We can review coverage options for your entire household in one request.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function StepGoals({ form, set, toggleArr }) {
  return (
    <div data-testid="step-goals">
      <Q>How much coverage are you considering?</Q>
      <OptionPills options={COVERAGE_AMOUNTS} value={form.coverage_amount}
                   onChange={(v) => set("coverage_amount", v)} testIdPrefix="coverage-amount" />

      <div className="mt-9">
        <Q>What type of coverage interests you?</Q>
        <Sub>Select all that apply — it's okay to pick more than one.</Sub>
        <CheckPills options={COVERAGE_TYPES} values={form.coverage_types}
                    onToggle={(v) => toggleArr("coverage_types", v)} testIdPrefix="coverage-type" />
      </div>

      <div className="mt-8 rounded-2xl bg-[#0B2319] border border-[#D4AF37]/40 p-6" data-testid="step2-guide-card">
        <p className="font-serif text-lg font-bold text-[#FCF6BA]">NOT SURE WHAT COVERAGE YOU NEED?</p>
        <p className="text-sm text-[#C9D6CE] mt-2 leading-relaxed">
          Choosing the right policy can feel confusing. Download my detailed Life Insurance Policy
          Guide to compare Term, Whole Life, IUL, Universal Life, Final Expense, Mortgage Protection,
          Living Benefits and more.
        </p>
        <a href="/assets/policy-guide.pdf" download data-testid="step2-download-guide-button"
           className="mt-4 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-[#C5A059] hover:bg-[#B8860B] text-[#0B2319] font-bold text-sm transition-colors w-full sm:w-auto">
          <FileDown size={16} /> DOWNLOAD MY POLICY GUIDE
        </a>
      </div>
    </div>
  );
}

function StepFinancial({ form, set, toggleArr }) {
  const fin = form.financial;
  return (
    <div data-testid="step-financial">
      <Q>If your income stopped tomorrow, approximately how much monthly income would your household need replaced?</Q>
      <OptionPills options={INCOME_OPTIONS} value={fin.monthly_income}
                   onChange={(v) => set("financial.monthly_income", v)} testIdPrefix="income" />

      <div className="mt-9">
        <Q>Do you currently have a mortgage?</Q>
        <OptionPills options={["Yes", "No"]} value={fin.has_mortgage}
                     onChange={(v) => set("financial.has_mortgage", v)} testIdPrefix="mortgage" />
        {fin.has_mortgage === "Yes" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <Field label="Approximate mortgage balance" testId="mortgage-balance-field">
              <input className={inputCls} value={fin.mortgage_balance} data-testid="mortgage-balance-input"
                     onChange={(e) => set("financial.mortgage_balance", e.target.value)} placeholder="e.g. $220,000" />
            </Field>
            <Field label="Approximate monthly payment" testId="mortgage-payment-field">
              <input className={inputCls} value={fin.mortgage_payment} data-testid="mortgage-payment-input"
                     onChange={(e) => set("financial.mortgage_payment", e.target.value)} placeholder="e.g. $1,650" />
            </Field>
          </div>
        )}
      </div>

      <div className="mt-9">
        <Q>Do you have children or financial dependents?</Q>
        <OptionPills options={["Yes", "No"]} value={fin.has_dependents}
                     onChange={(v) => set("financial.has_dependents", v)} testIdPrefix="dependents" />
        {fin.has_dependents === "Yes" && (
          <div className="mt-4 max-w-[160px]">
            <Field label="How many?" testId="dependents-count-field">
              <input className={inputCls} value={fin.dependents_count} data-testid="dependents-count-input"
                     onChange={(e) => set("financial.dependents_count", e.target.value)} placeholder="e.g. 2" />
            </Field>
          </div>
        )}
      </div>

      <div className="mt-9">
        <Q>Would you like your coverage to help with any of the following?</Q>
        <Sub>Select all that apply.</Sub>
        <CheckPills options={HELP_WITH} values={fin.help_with}
                    onToggle={(v) => toggleArr("financial.help_with", v)} testIdPrefix="help-with" />
      </div>
    </div>
  );
}

function StepHealth({ form, set, toggleArr }) {
  const h = form.health;
  return (
    <div data-testid="step-health">
      <Q>A quick health snapshot</Q>
      <Sub>This is preliminary only — no underwriting decisions are made here.</Sub>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Height" testId="height-field">
          <input className={inputCls} value={h.height} data-testid="height-input"
                 onChange={(e) => set("health.height", e.target.value)} placeholder={`e.g. 5'7"`} />
        </Field>
        <Field label="Weight" testId="weight-field">
          <input className={inputCls} value={h.weight} data-testid="weight-input"
                 onChange={(e) => set("health.weight", e.target.value)} placeholder="e.g. 160 lbs" />
        </Field>
      </div>

      <div className="mt-7">
        <Q>Tobacco / nicotine use?</Q>
        <OptionPills options={["Yes", "No"]} value={h.tobacco}
                     onChange={(v) => set("health.tobacco", v)} testIdPrefix="tobacco" />
        {h.tobacco === "Yes" && (
          <div className="mt-4">
            <Field label="Type and approximate last use" testId="tobacco-details-field">
              <input className={inputCls} value={h.tobacco_details} data-testid="tobacco-details-input"
                     onChange={(e) => set("health.tobacco_details", e.target.value)}
                     placeholder="e.g. Cigarettes, last used 2 years ago" />
            </Field>
          </div>
        )}
      </div>

      <div className="mt-9">
        <Q>Have you been diagnosed with or treated for any major medical condition?</Q>
        <CheckPills options={CONDITIONS} values={h.conditions}
                    onToggle={(v) => toggleArr("health.conditions", v)} testIdPrefix="condition" />
        <div className="mt-4">
          <Field label="Brief notes (optional)" testId="condition-notes-field">
            <textarea className={`${inputCls} h-24 py-3 resize-none`} value={h.condition_notes}
                      data-testid="condition-notes-input"
                      onChange={(e) => set("health.condition_notes", e.target.value)}
                      placeholder="Anything you'd like me to know" />
          </Field>
        </div>
      </div>

      <div className="mt-9">
        <Q>Are you currently taking prescription medication?</Q>
        <OptionPills options={["Yes", "No"]} value={h.medications}
                     onChange={(v) => set("health.medications", v)} testIdPrefix="medications" />
        {h.medications === "Yes" && (
          <div className="mt-4">
            <Field label="Medication names" testId="medication-names-field">
              <input className={inputCls} value={h.medication_names} data-testid="medication-names-input"
                     onChange={(e) => set("health.medication_names", e.target.value)}
                     placeholder="e.g. Lisinopril, Metformin" />
            </Field>
          </div>
        )}
      </div>

      <div className="mt-8 rounded-xl bg-[#FAF4E8] border border-[#D4AF37]/40 px-4 py-3" data-testid="health-underwriting-note">
        <p className="text-xs text-[#0B2319] leading-relaxed">
          Final eligibility, classification and premium are determined by the insurance carrier after
          underwriting.
        </p>
      </div>
    </div>
  );
}

function StepCurrent({ form, set }) {
  const cc = form.current_coverage;
  return (
    <div data-testid="step-current">
      <Q>Do you currently have life insurance?</Q>
      <OptionPills options={["Yes", "No", "I'm not sure"]} value={cc.has_life_insurance}
                   onChange={(v) => set("current_coverage.has_life_insurance", v)} testIdPrefix="has-insurance" />

      {cc.has_life_insurance === "Yes" && (
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Approximate coverage amount" testId="existing-amount-field">
              <input className={inputCls} value={cc.existing_amount} data-testid="existing-amount-input"
                     onChange={(e) => set("current_coverage.existing_amount", e.target.value)} placeholder="e.g. $250,000" />
            </Field>
            <Field label="Type of policy (if known)" testId="existing-type-field">
              <input className={inputCls} value={cc.existing_type} data-testid="existing-type-input"
                     onChange={(e) => set("current_coverage.existing_type", e.target.value)} placeholder="e.g. Term, Whole Life" />
            </Field>
          </div>
          <Field label="Is this employer coverage, personally owned coverage, or both?" testId="existing-ownership-field">
            <OptionPills options={["Employer coverage", "Personally owned", "Both"]} value={cc.existing_ownership}
                         onChange={(v) => set("current_coverage.existing_ownership", v)} testIdPrefix="ownership" />
          </Field>
        </div>
      )}

      <div className="mt-9">
        <Q>Are you looking to:</Q>
        <OptionPills
          options={["Add coverage", "Replace existing coverage", "Review current coverage", "Purchase first policy", "Not sure yet"]}
          value={cc.looking_to} onChange={(v) => set("current_coverage.looking_to", v)} testIdPrefix="looking-to" />
        {cc.looking_to === "Replace existing coverage" && (
          <div className="mt-4 rounded-xl bg-[#FAF4E8] border border-[#D4AF37]/40 px-4 py-3" data-testid="replacement-warning">
            <p className="text-xs text-[#0B2319] leading-relaxed">
              Replacing an existing policy may require additional disclosures and should only occur
              after reviewing your current policy and new coverage.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function StepContact({ form, set }) {
  const c = form.contact;
  return (
    <div data-testid="step-contact">
      <Q>Where should I reach you?</Q>
      <Sub>I'll review your request personally and compare options before we talk.</Sub>
      <div className="space-y-4">
        <Field label="Full name" testId="contact-name-field">
          <input className={inputCls} value={c.full_name} data-testid="contact-name-input"
                 onChange={(e) => set("contact.full_name", e.target.value)} placeholder="First and last name" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Phone" testId="contact-phone-field">
            <input className={inputCls} value={c.phone} data-testid="contact-phone-input" type="tel"
                   onChange={(e) => set("contact.phone", e.target.value)} placeholder="(205) 555-0123" />
          </Field>
          <Field label="Email" testId="contact-email-field">
            <input className={inputCls} value={c.email} data-testid="contact-email-input" type="email"
                   onChange={(e) => set("contact.email", e.target.value)} placeholder="you@email.com" />
          </Field>
        </div>
        <Field label="Preferred contact method" testId="preferred-contact-field">
          <OptionPills options={["Call", "Text", "Email"]} value={c.preferred_contact}
                       onChange={(v) => set("contact.preferred_contact", v)} testIdPrefix="preferred-contact" />
        </Field>
        <Field label="Best time to contact" testId="best-time-field">
          <OptionPills options={["Morning", "Afternoon", "Evening"]} value={c.best_time}
                       onChange={(v) => set("contact.best_time", v)} testIdPrefix="best-time" />
        </Field>
      </div>

      <div className="mt-7 space-y-3">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-info-label">
          <input type="checkbox" checked={c.consent_info} data-testid="consent-info-checkbox"
                 onChange={(e) => set("contact.consent_info", e.target.checked)}
                 className="mt-0.5 w-5 h-5 rounded accent-[#0B2319]" />
          <span className="text-xs text-[#1A231F] leading-relaxed">
            I understand this is a request for information / a quote and does not create insurance coverage.
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-contact-label">
          <input type="checkbox" checked={c.consent_contact} data-testid="consent-contact-checkbox"
                 onChange={(e) => set("contact.consent_contact", e.target.checked)}
                 className="mt-0.5 w-5 h-5 rounded accent-[#0B2319]" />
          <span className="text-xs text-[#1A231F] leading-relaxed">
            I consent to be contacted by Simonne &amp; Co. Financial Solutions at the phone number and
            email provided regarding my quote request.
          </span>
        </label>
      </div>
    </div>
  );
}
